'use client'

import React, { useState, useEffect } from "react";
import { Menu, User, LogOut } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { User as UserType } from "@/lib/auth-api";
import { useAuthStore } from "@/stores/auth-store";
import { apiService } from "@/lib/api";
import { getAccessToken } from "@/lib/utils";

interface HeaderProps {
  onMenuClick: () => void;
  title?: string;
  subtitle?: string;
  user?: UserType | null;
  onLogout?: () => Promise<void>;
}

export const Header: React.FC<HeaderProps> = ({ 
  onMenuClick, 
  title = "SaarthiQ", 
  subtitle = "Welcome back!",
  user: propUser,
  onLogout
}) => {
  const { user: storeUser, logout: storeLogout } = useAuthStore();
  const user = propUser || storeUser;
  const logout = onLogout || storeLogout;
  const [shopName, setShopName] = useState<string>("SaarthiQ");

  // Fetch shop profile for header title only when authenticated
  useEffect(() => {
    const fetchShopProfile = async () => {
      try {
        // Check if we have a valid access token before making API call
        const token = getAccessToken();
        if (!token) {
          console.log('Header: No access token available, skipping shop profile fetch');
          return;
        }

        const profile = await apiService.getShopProfile();
        if (profile?.display_name) {
          setShopName(profile.display_name);
        } else if (profile?.shop_name) {
          setShopName(profile.shop_name);
        }
      } catch (error) {
        console.warn('Failed to fetch shop profile for header:', error);
        // Keep default "SaarthiQ" as fallback
      }
    };

    // Only fetch shop profile if user is authenticated and we have a token
    if (user && getAccessToken()) {
      fetchShopProfile();
    }
  }, [user]);

  return (
    <div className="no-print bg-card border-b border-border px-4 py-4 md:px-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={onMenuClick}
            className="lg:hidden"
          >
            <Menu className="w-5 h-5" />
          </Button>
          <div className="flex items-center gap-3">
            <img 
              src="/saarthiq_symbol_logo.png"
              alt="SaarthiQ Logo" 
              className="h-8 w-auto"
            />
            <div>
              <h1 className="text-xl font-bold text-foreground">
                {shopName}
              </h1>
            </div>
          </div>
        </div>



        <div className="flex items-center gap-2">
          {/* User Menu */}
          {user && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="relative h-8 w-8 rounded-full">
                  <Avatar className="h-8 w-8">
                    <AvatarImage src="" alt={user.first_name} />
                    <AvatarFallback className="bg-primary text-primary-foreground">
                      {user.first_name?.[0]}{user.last_name?.[0]}
                    </AvatarFallback>
                  </Avatar>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-56" align="end" forceMount>
                <DropdownMenuItem className="flex flex-col items-start">
                  <div className="flex items-center gap-2">
                    <User className="w-4 h-4" />
                    <span className="font-medium">{user.first_name} {user.last_name}</span>
                  </div>
                  <span className="text-sm text-muted-foreground">{user.email}</span>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={logout} className="text-destructive">
                  <LogOut className="w-4 h-4 mr-2" />
                  Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>
    </div>
  );
};

export default Header; 